import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { ChevronRight } from "@mui/icons-material";
import { Button } from "@mui/material";

import { SimpleMarkdown } from "@components/SimpleMarkdown";
import { useMediaQueryBreakpoint, usePerformance } from "@hooks";
import { WorkingArea } from "@utils";

import {
  GridContainer,
  GridItemHeader,
  GridItemMiddle,
  GridItemWithDot,
} from "./Specializations.styled";

interface HeaderContent {
  splineCodeSrc?: string;
  fallbackSrc?: string;
  title: string;
  description: string;
  seeMoreText: string;
}

interface SpecializationsSectionProps {
  colorKey: WorkingArea;
  ctaText: string;
  ctaBaseText: string;
  href?: string;
  seeMoreHref?: string;
  header?: HeaderContent;
  contents: {
    header: string;
    body: string;
  }[];
}

const Spline = dynamic(
  () =>
    import("@baseComponents/SplineWrapper").then(mod => ({
      default: mod.SplineWrapper,
    })),
  {
    ssr: false,
    loading: () => <div />,
  },
);

export const SpecializationsSection = ({
  colorKey,
  ctaText,
  ctaBaseText,
  href = "/projects",
  seeMoreHref,
  header,
  contents,
}: SpecializationsSectionProps) => {
  const { isDesktop } = useMediaQueryBreakpoint();
  const { splineSupport } = usePerformance();
  const resolvedSeeMoreHref = seeMoreHref ?? href;

  const sharedStyle = {
    width: "128px",
    height: "128px",
  };

  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <GridContainer ref={containerRef} colorKey={colorKey}>
      {header && (
        <GridItemHeader>
          {splineSupport && isVisible
            ? header.splineCodeSrc && (
                <Spline
                  style={sharedStyle}
                  scene={header.splineCodeSrc}
                  onLoad={scene => scene.setZoom(1.5)}
                />
              )
            : header.fallbackSrc && (
                <Image
                  src={header.fallbackSrc}
                  width={Number(sharedStyle.width.replace("px", ""))}
                  height={Number(sharedStyle.height.replace("px", ""))}
                  alt=""
                  style={sharedStyle}
                />
              )}

          <h3>{header.title}</h3>

          <p>{header.description}</p>

          <Link href={resolvedSeeMoreHref}>
            <Button
              style={{ paddingLeft: 0 }}
              variant="text"
              endIcon={<ChevronRight />}
            >
              {header.seeMoreText}
            </Button>
          </Link>
        </GridItemHeader>
      )}

      {contents.length > 0 && (
        <GridItemWithDot corner="bottom-left" style={{ gridArea: "item1" }}>
          <h4>{contents[0].header}</h4>

          <SimpleMarkdown>{contents[0].body}</SimpleMarkdown>
        </GridItemWithDot>
      )}

      {contents.length > 1 && (
        <GridItemWithDot corner="bottom-right" style={{ gridArea: "item2" }}>
          <h4>{contents[1].header}</h4>

          <SimpleMarkdown>{contents[1].body}</SimpleMarkdown>
        </GridItemWithDot>
      )}

      {isDesktop && (
        <GridItemMiddle>
          <span>
            {ctaBaseText}

            <Link href={href}>
              <Button variant="text" endIcon={<ChevronRight />}>
                {ctaText}
              </Button>
            </Link>
          </span>
        </GridItemMiddle>
      )}

      {contents.length > 2 && (
        <GridItemWithDot corner="top-left" style={{ gridArea: "item3" }}>
          <h4>{contents[2].header}</h4>

          <SimpleMarkdown>{contents[2].body}</SimpleMarkdown>
        </GridItemWithDot>
      )}

      {contents.length > 3 && (
        <GridItemWithDot corner="top-right" style={{ gridArea: "item4" }}>
          <h4>{contents[3].header}</h4>

          <SimpleMarkdown>{contents[3].body}</SimpleMarkdown>
        </GridItemWithDot>
      )}
    </GridContainer>
  );
};
